import React, { createContext, useState, useMemo } from "react";
import { createTheme } from "@mui/material";
import {
  typographyTokens,
  colorTokens,
  fontStack,
  mapFontWeight,
  parseFontSize,
} from "./styles/designTokens";

// colors
export const tokens = (mode) => ({
  ...(mode === "dark"
    ? {
        grey: {
          100: "#e0e0e0",
          200: "#c2c2c2",
          300: "#a3a3a3",
          400: "#858585",
          500: "#666666",
          600: "#525252",
          700: "#3d3d3d",
          800: "#292929",
          900: "#141414",
        },

        primary: {
          100: "#d0d1d5",
          200: "#a1a4aa",
          300: "#727680",
          400: "#1F2A40",
          500: "#141b2b",
          600: "#240046",
          700: "#0c101a",
          800: "#080b11",
          900: "#040509",
        },
        greenAccent: {
          100: "#dbf5ee",
          200: "#b7ebde",
          300: "#94e2cd",
          400: "#70d8bd",
          500: "#4cceac",
          600: "#3da58a",
          700: "#2e7c67",
          800: "#1e5245",
          900: "#0f2922",
        },
        redAccent: {
          100: "#f8dcdb",
          200: "#f1b9b7",
          300: "#e99592",
          400: "#e2726e",
          500: "#db4f4a",
          600: "#af3f3b",
          700: "#832f2c",
          800: "#58201e",
          900: "#2c100f",
        },
        blueAccent: {
          100: "#e1e2fe",
          200: "#c3c6fd",
          300: "#a4a9fc",
          400: "#868dfb",
          500: "#6870fa",
          600: "#535ac8",
          700: "#3e4396",
          800: "#2a2d64",
          900: "#151632",
        },
      }
    : {
        grey: {
          100: "#141414",
          200: "#292929",
          300: "#3d3d3d",
          400: "#525252",
          500: "#666666",
          600: "#858585",
          700: "#a3a3a3",
          800: "#c2c2c2",
          900: "#e0e0e0",
        },
        primary: {
          100: "#040509",
          200: "#080b11",
          300: "#0c101a",
          400: "#f1f1f1", // manually changed
          500: "#141b2b",
          600: "#434955",
          700: "#727680",
          800: "#a1a4aa",
          900: "#d0d1d5",
        },
        greenAccent: {
          100: "#0f2922",
          200: "#1e5245",
          300: "#2e7c67",
          400: "#3da58a",
          500: "#4cceac",
          600: "#70d8bd",
          700: "#94e2cd",
          800: "#b7ebde",
          900: "#dbf5ee",
        },
        redAccent: {
          100: "#2c100f",
          200: "#58201e",
          300: "#832f2c",
          400: "#af3f3b",
          500: "#db4f4a",
          600: "#e2726e",
          700: "#e99592",
          800: "#f1b9b7",
          900: "#f8dcdb",
        },
        blueAccent: {
          100: "#151632",
          200: "#2a2d64",
          300: "#3e4396",
          400: "#535ac8",
          500: "#6870fa",
          600: "#868dfb",
          700: "#a4a9fc",
          800: "#c3c6fd",
          900: "#e1e2fe",
        },
      }),
});

const CHECKBOX_ICON_CLASS = "kala-checkbox-icon";
const baseCheckboxIcon = (
  <span className={CHECKBOX_ICON_CLASS} aria-hidden="true" />
);
const checkedCheckboxIcon = (
  <span
    className={`${CHECKBOX_ICON_CLASS} ${CHECKBOX_ICON_CLASS}--checked`}
    aria-hidden="true"
  />
);
const indeterminateCheckboxIcon = (
  <span
    className={`${CHECKBOX_ICON_CLASS} ${CHECKBOX_ICON_CLASS}--indeterminate`}
    aria-hidden="true"
  />
);

const hexToRgba = (hex, alpha = 1) => {
  if (!hex) return `rgba(0,0,0,${alpha})`;
  let sanitized = hex.replace("#", "");
  if (sanitized.length === 3) {
    sanitized = sanitized
      .split("")
      .map((char) => `${char}${char}`)
      .join("");
  }
  const bigint = parseInt(sanitized, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

//mui theme settings
export const themeSettings = (mode) => {
  const colors = tokens(mode);
  const checkboxOutlineColor =
    colorTokens.borderSecondary ||
    colorTokens.interactionOutlineBase ||
    "#cbd5f5";
  const checkboxBackground = colorTokens.backgroundPrimary || "#ffffff";
  const checkboxSelectedColor = colorTokens.interactionBlueBase || "#2563eb";
  const checkboxSelectedHover = colorTokens.interactionBlueHover || "#1d4ed8";
  const checkboxSelectedActive = colorTokens.interactionBlueActive || "#1e40af";
  const checkboxCheckColor = colorTokens.contentLightPrimary || "#ffffff";
  const checkboxFocusShadow = hexToRgba(checkboxSelectedColor, 0.18);

  return {
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
            info: {
              main: "red",
            },
            primary: {
              main: colors.primary[500],
            },
            secondary: {
              main: colors.greenAccent[500],
            },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            background: {
              default: colorTokens.backgroundSecondary || colors.primary[400],
              paper: colorTokens.backgroundPrimary || colors.primary[400],
            },
          }
        : {
            primary: {
              main: colors.primary[100],
            },
            secondary: {
              main: colors.greenAccent[500],
            },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            background: {
              default: colorTokens.backgroundSecondary || colors.primary[400],
              paper: colorTokens.backgroundPrimary || "#ffffff",
            },
          }),
    },
    typography: {
      fontFamily: fontStack,
      fontSize: parseFontSize(typographyTokens.bodyRegular.fontSize) || 14,
      lineHeight: typographyTokens.bodyRegular.lineHeight,
      h1: {
        fontFamily: fontStack,
        fontSize: parseFontSize(typographyTokens.displayLarge.fontSize) || 40,
        lineHeight: typographyTokens.displayLarge.lineHeight,
        fontWeight: mapFontWeight(typographyTokens.displayLarge.fontWeight),
      },
      h2: {
        fontFamily: fontStack,
        fontSize: parseFontSize(typographyTokens.displaySmall.fontSize) || 32,
        lineHeight: typographyTokens.displaySmall.lineHeight,
        fontWeight: mapFontWeight(typographyTokens.displaySmall.fontWeight),
      },
      h3: {
        fontFamily: fontStack,
        fontSize: parseFontSize(typographyTokens.headingLarge.fontSize) || 24,
        lineHeight: typographyTokens.headingLarge.lineHeight,
        fontWeight: mapFontWeight(typographyTokens.headingLarge.fontWeight),
      },
      h4: {
        fontFamily: fontStack,
        fontSize: parseFontSize(typographyTokens.headingMedium.fontSize) || 20,
        lineHeight: typographyTokens.headingMedium.lineHeight,
        fontWeight: mapFontWeight(typographyTokens.headingMedium.fontWeight),
      },
      h5: {
        fontFamily: fontStack,
        fontSize: parseFontSize(typographyTokens.headingSmall.fontSize) || 16,
        lineHeight: typographyTokens.headingSmall.lineHeight,
        fontWeight: mapFontWeight(typographyTokens.headingSmall.fontWeight),
      },
      h6: {
        fontFamily: fontStack,
        fontSize: parseFontSize(typographyTokens.bodyMedium.fontSize) || 14,
        lineHeight: typographyTokens.bodyMedium.lineHeight,
        fontWeight: mapFontWeight(typographyTokens.bodyMedium.fontWeight),
      },
      body1: {
        fontFamily: fontStack,
        fontSize: parseFontSize(typographyTokens.bodyRegular.fontSize) || 14,
        lineHeight: typographyTokens.bodyRegular.lineHeight,
        fontWeight: mapFontWeight(typographyTokens.bodyRegular.fontWeight),
      },
      body2: {
        fontFamily: fontStack,
        fontSize: parseFontSize(typographyTokens.smallRegular.fontSize) || 12,
        lineHeight: typographyTokens.smallRegular.lineHeight,
        fontWeight: mapFontWeight(typographyTokens.smallRegular.fontWeight),
      },
      button: {
        fontFamily: fontStack,
        fontSize: parseFontSize(typographyTokens.bodyMedium.fontSize) || 14,
        lineHeight: typographyTokens.bodyMedium.lineHeight,
        fontWeight: mapFontWeight(typographyTokens.bodyMedium.fontWeight),
        textTransform: "none",
      },
    },
    components: {
      MuiCheckbox: {
        defaultProps: {
          disableRipple: true,
          icon: baseCheckboxIcon,
          checkedIcon: checkedCheckboxIcon,
          indeterminateIcon: indeterminateCheckboxIcon,
        },
        styleOverrides: {
          root: {
            width: 22,
            height: 22,
            padding: 0,
            margin: 0,
            borderRadius: 4,
            color: checkboxOutlineColor,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "transform 150ms ease",
            "&:hover": {
              backgroundColor: "transparent",
            },
            "&:hover .kala-checkbox-icon": {
              borderColor: checkboxSelectedColor,
            },
            "&:hover .kala-checkbox-icon--checked": {
              backgroundColor: checkboxSelectedHover,
              borderColor: checkboxSelectedHover,
            },
            "&:active .kala-checkbox-icon": {
              borderColor: checkboxSelectedActive,
            },
            "&:active .kala-checkbox-icon--checked": {
              backgroundColor: checkboxSelectedActive,
              borderColor: checkboxSelectedActive,
            },
            "&.Mui-focusVisible .kala-checkbox-icon": {
              boxShadow: `0 0 0 3px ${checkboxFocusShadow}`,
            },
            "& .kala-checkbox-icon": {
              width: 18,
              height: 18,
              borderRadius: 4,
              border: `1.5px solid ${checkboxOutlineColor}`,
              backgroundColor: checkboxBackground,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 120ms ease",
              position: "relative",
              boxSizing: "border-box",
            },
            "& .kala-checkbox-icon::after": {
              content: '""',
              width: 8,
              height: 4,
              borderLeft: `2px solid ${checkboxCheckColor}`,
              borderBottom: `2px solid ${checkboxCheckColor}`,
              transform: "rotate(-45deg) scale(0)",
              transformOrigin: "center",
              transition: "transform 150ms ease",
            },
            "& .kala-checkbox-icon--checked": {
              borderColor: checkboxSelectedColor,
              backgroundColor: checkboxSelectedColor,
              boxShadow: `0 2px 6px ${hexToRgba(checkboxSelectedColor, 0.35)}`,
            },
            "& .kala-checkbox-icon--checked::after": {
              transform: "rotate(-45deg) scale(1)",
            },
            "& .kala-checkbox-icon--indeterminate": {
              borderColor: checkboxSelectedColor,
              backgroundColor: checkboxSelectedColor,
              boxShadow: `0 2px 6px ${hexToRgba(checkboxSelectedColor, 0.35)}`,
            },
            "& .kala-checkbox-icon--indeterminate::after": {
              width: 10,
              height: 0,
              border: "none",
              borderBottom: `2px solid ${checkboxCheckColor}`,
              transform: "scale(1)",
            },
            "&.Mui-disabled .kala-checkbox-icon": {
              opacity: 0.5,
              borderColor: checkboxOutlineColor,
              backgroundColor: colorTokens.backgroundSecondary || "#f8fafc",
              boxShadow: "none",
            },
          },
        },
      },
    },
  };
};

//context color mode
export const ColorModeContext = createContext({
  toggleColorMode: () => {},
});

export const useMode = () => {
  const [mode, setMode] = useState("light");

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () =>
        setMode((prev) => (prev === "light" ? "dark" : "light")),
    }),
    []
  );

  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);

  return [theme, colorMode];
};
